import * as React from 'react';
import { FiX, FiRefreshCw } from 'react-icons/fi';
import { cn } from '@/lib/utils';
import tiaAvatar from '@/assets/images/tia/tia-avatar-3d.png';
import tiaBoasVindas from '@/assets/images/tia/TIA-BOAS-VINDAS.png';
import tiaPensando from '@/assets/images/tia/TIA-PENSANDO.png';
import tiaOps from '@/assets/images/tia/TIA-OPS.png';
import { formatTiaMessage } from './format-tia-message';

export function TiaChatSheet({ messages, status, onRetry, onClose, closing }) {
  const scrollRef = React.useRef(null);
  const sectionRef = React.useRef(null);

  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, status]);

  React.useEffect(() => {
    sectionRef.current?.focus();
  }, []);

  React.useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <>
      <div
        className={cn('fixed inset-0 z-[101]', closing && 'pointer-events-none')}
        onClick={onClose}
        aria-hidden="true"
      />
      <section
        id="tia-chat-sheet"
        ref={sectionRef}
        tabIndex={-1}
        aria-label="Conversa com a T.I.A"
        className={cn(
          'c-tia-chat-sheet',
          closing && 'c-tia-chat-sheet--closing',
          'fixed bottom-[calc(10.5rem+env(safe-area-inset-bottom))] left-1/2 z-[102] -translate-x-1/2',
          'flex max-h-[55vh] w-[calc(100%-2rem)] max-w-xl flex-col overflow-hidden',
          'focus:outline-none',
          // Degradê bem sutil: sussurro de lilás no topo esvaindo pro quase
          // branco (nunca branco puro).
          'rounded-3xl border border-neutral-lighter bg-gradient-to-b from-[#FAF8FF] to-[#FDFCFF] shadow-[0_24px_48px_rgba(0,0,0,0.2)]'
        )}
      >
        <header className="flex items-center gap-2 border-b border-neutral-lighter px-4 py-3">
          <img
            src={tiaAvatar}
            alt=""
            className="h-8 w-8 rounded-full"
          />
          <span className="flex-1 text-sm font-bold text-neutral-dark">T.I.A</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar conversa"
            className="flex h-11 w-11 items-center justify-center rounded-full text-neutral-dark transition-transform active:scale-95"
          >
            <FiX size={18} />
          </button>
        </header>

        <div
          ref={scrollRef}
          aria-live="polite"
          className="flex-1 space-y-3 overflow-y-auto px-4 py-3"
        >
          {messages.length === 0 && status === 'idle' && (
            <div className="flex flex-col items-center gap-2 py-6 text-center">
              <img
                src={tiaBoasVindas}
                alt=""
                className="h-28 w-auto"
              />
              <p className="text-sm font-semibold text-neutral-dark">Oi! Eu sou a T.I.A 💜</p>
              <p className="text-xs text-neutral-dark">Me pergunte qualquer coisa sobre seus estudos ou sobre a plataforma!</p>
            </div>
          )}

          {messages.map(message => (
            <div
              key={message.id}
              className={cn('flex items-end gap-2', message.role === 'student' && 'justify-end')}
            >
              {message.role === 'tia' && (
                <img
                  src={tiaAvatar}
                  alt=""
                  className="h-6 w-6 rounded-full"
                />
              )}
              <p
                className={cn(
                  'max-w-[80%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[0.925rem]',
                  message.role === 'tia' ? 'rounded-bl-sm bg-white text-[#110b3e] shadow-[0_1px_4px_rgba(93,106,134,0.15)]' : 'rounded-br-sm bg-secondary text-white'
                )}
              >
                {message.role === 'tia' ? formatTiaMessage(message.body) : message.body}
              </p>
            </div>
          ))}

          {status === 'thinking' && (
            <div className="flex items-center gap-2">
              <img
                src={tiaPensando}
                alt=""
                className="h-16 w-auto"
              />
              <p className="text-xs font-medium text-neutral-dark">A T.I.A está pensando...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center gap-2 py-2 text-center">
              <img
                src={tiaOps}
                alt=""
                className="h-20 w-auto"
              />
              <p className="text-xs text-neutral-dark">A T.I.A se atrapalhou, tenta de novo?</p>
              <button
                type="button"
                onClick={onRetry}
                className="flex items-center gap-1.5 rounded-full bg-secondary px-4 py-1.5 text-xs font-bold text-white transition-transform active:scale-95"
              >
                <FiRefreshCw size={12} /> Tentar de novo
              </button>
            </div>
          )}

          {status === 'limit' && (
            <div className="flex flex-col items-center gap-2 py-2 text-center">
              <img
                src={tiaOps}
                alt=""
                className="h-20 w-auto"
              />
              <p className="text-xs text-neutral-dark">Por hoje chega! Amanhã a T.I.A responde mais 💜</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
